import { Link } from "react-router-dom";
import React from "react";
import { IPlayer } from "../../../types/types";
import { useLocalePath } from "../../../hooks/useLocalePath";
import { getPlayerProfilePath } from "../../../utils/playerProfilePath";
import { championIconById, rankedCrestUrl, ROLE_ICONS, type PlayerPosition } from "../constants";

export type { PlayerPosition } from "../constants";

export const PlayerCard: React.FC<{
  player: IPlayer;
  linkToProfile?: boolean;
  className?: string;
}> = ({ player, linkToProfile = false, className = "" }) => {
  const { localized } = useLocalePath();
  const hasRank =
    player.rank && player.rank.trim().length > 0 && player.rank.toLowerCase() !== "unranked";

  const tier = hasRank ? player.rank.trim().split(" ")[0].toLowerCase() : "unranked";
  const displayRank = hasRank ? player.rank : "Unranked";
  const rankIconUrl = rankedCrestUrl(tier);
  const profilePath = linkToProfile ? localized(getPlayerProfilePath(player.name)) : undefined;
  const roleKey = player.position.toUpperCase();
  const roleSrc = roleKey in ROLE_ICONS ? ROLE_ICONS[roleKey as PlayerPosition] : ROLE_ICONS.FILL;

  const card = (
    <div
      className={`flex h-full min-h-[22rem] flex-col rounded-[var(--surface-card-radius)] border border-[var(--surface-card-border)] bg-[var(--surface-card-bg)] p-[1.2rem] text-center box-border max-[720px]:min-h-[19rem] max-[720px]:p-4 max-[420px]:min-h-0 ${
        profilePath
          ? "cursor-pointer transition-[border-color,transform] duration-150 ease-in-out hover:-translate-y-0.5 hover:border-[var(--surface-card-hover-border)]"
          : ""
      } ${className}`}
    >
      <div className="mb-4 flex flex-col items-center">
        <img
          src={roleSrc}
          alt={player.position}
          className="mb-2 h-[30px] w-[30px] brightness-125 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
          width={30}
          height={30}
          loading="lazy"
          decoding="async"
        />
        <div className="w-full overflow-hidden">
          <p className="fh-title-card truncate">{player.name}</p>
          <p className="fh-meta mt-0.5 mb-0 text-[#5cc2ff]">{player.position}</p>
        </div>
      </div>

      <div className="relative flex min-h-[140px] flex-col items-center justify-center rounded-[10px] border border-[#2d2d44] bg-[rgba(45,45,68,0.4)] p-4">
        <img
          src={rankIconUrl}
          alt={displayRank}
          className="mb-auto flex h-[50px] w-[50px] items-center justify-center object-contain"
          width={50}
          height={50}
          loading="lazy"
          decoding="async"
        />
        <p className="m-0 font-shentox text-[0.85rem] font-bold uppercase text-[#eee]">
          {displayRank}
        </p>
        {hasRank ? (
          <p className="mt-auto mb-0 font-shentox text-[0.85rem] font-semibold text-[#ffcc33]">
            {player.leaguePoint} LP
          </p>
        ) : null}
      </div>

      {profilePath ? (
        <p className="fh-caption mt-3 mb-0 border-t border-[var(--surface-card-border)] pt-3 text-light-blue">
          Näytä pelihistoria →
        </p>
      ) : null}

      <div className="mt-auto border-t border-[#2d2d44] pt-4">
        <div className="flex items-end justify-center gap-[0.8rem]">
          {player.championMastery.map((champ) => (
            <div key={champ.championId} className="flex flex-col items-center">
              <div className="relative h-9 w-9 rounded-full bg-gradient-to-b from-[#3a3a5a] to-[var(--surface-card-bg)] p-0.5 shadow-[0_2px_8px_rgba(0,0,0,0.4)]">
                <img
                  src={championIconById(champ.championId)}
                  className="block h-full w-full rounded-full object-cover"
                  alt=""
                  width={32}
                  height={32}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <span className="mt-1 text-[0.6rem] font-medium text-[#7a7a9a]">
                Lv{champ.championLevel}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (profilePath) {
    return (
      <Link to={profilePath} className="block h-full min-w-0 text-inherit no-underline">
        {card}
      </Link>
    );
  }

  return card;
};
